import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Home as HomeIcon,
  CreateNewFolder as CreateFolderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useFolderContext } from '../../context/FolderContext';
import { folderService } from '../../services/folderService';
import { useAuth } from '../../context/AuthContext';

const InlineFolderSelector = ({ 
  variant = 'button', // 'button', 'chip', 'compact'
  showCreateNew = true,
  placeholder = 'Select folder...',
  size = 'medium',
  sx = {}
}) => {
  const theme = useTheme();
  const { user } = useAuth() || { user: null };
  const { selectedFolderId, selectFolder } = useFolderContext();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
  const [contextMenuFolder, setContextMenuFolder] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolderInfo, setSelectedFolderInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editFolderName, setEditFolderName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [parentFolderId, setParentFolderId] = useState(null);

  const open = Boolean(anchorEl);
  const contextMenuOpen = Boolean(contextMenuAnchor);

  // Fetch folders and auto-select root folder
  useEffect(() => {
    const fetchFolders = async () => {
      if (!user?.userId) return;
      
      setLoading(true);
      try {
        const response = await folderService.getFolderTree();
        const folderData = response.data || [];
        setFolders(folderData);
        
        // Auto-select root folder if no folder is currently selected and folders are available
        if (!selectedFolderId && folderData.length > 0) {
          const rootFolder = folderData[0]; // First folder should be root
          if (rootFolder && (rootFolder.id === user.userId || rootFolder.folderId === user.userId)) {
            selectFolder(rootFolder.id || rootFolder.folderId);
          }
        }
      } catch (error) {
        console.error('Error fetching folders:', error);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we don't have folders yet or user changed
    if (!folders.length || !selectedFolderId) {
      fetchFolders();
    }
  }, [user?.userId]); // Removed selectedFolderId and selectFolder from dependencies to prevent loops

  // Update selected folder info
  useEffect(() => {
    if (selectedFolderId && folders.length > 0) {
      const findFolder = (folderList, targetId) => {
        for (const folder of folderList) {
          if (folder.id === targetId || folder.folderId === targetId) {
            return folder;
          }
          if (folder.children && folder.children.length > 0) {
            const found = findFolder(folder.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const folderInfo = findFolder(folders, selectedFolderId);
      setSelectedFolderInfo(folderInfo);
    } else {
      setSelectedFolderInfo(null);
    }
  }, [selectedFolderId, folders]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleContextMenu = (event, folder) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuAnchor(event.currentTarget);
    setContextMenuFolder(folder);
  };

  const handleContextMenuClose = () => {
    setContextMenuAnchor(null);
    setContextMenuFolder(null);
  };

  const handleFolderSelect = (folder) => {
    selectFolder(folder.id || folder.folderId);
    handleClose();
  };

  const handleCreateFolder = async (parentId = null) => {
    if (!newFolderName.trim()) return;

    setCreateLoading(true);
    try {
      const response = await folderService.createFolder({
        name: newFolderName.trim(),
        parentId: parentId
      });
      
      // Refresh folders
      const foldersResponse = await folderService.getFolderTree();
      setFolders(foldersResponse.data || []);
      
      // Select the newly created folder
      if (response.data) {
        selectFolder(response.data.id || response.data.folderId);
      }
      
      setNewFolderName('');
      setCreateDialogOpen(false);
      setParentFolderId(null);
    } catch (error) {
      console.error('Error creating folder:', error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditFolder = async () => {
    if (!editFolderName.trim() || !contextMenuFolder) return;

    try {
      await folderService.updateFolder(contextMenuFolder.id || contextMenuFolder.folderId, {
        name: editFolderName.trim()
      });
      
      // Refresh folders
      const foldersResponse = await folderService.getFolderTree();
      setFolders(foldersResponse.data || []);
      
      setEditFolderName('');
      setEditDialogOpen(false);
      handleContextMenuClose();
    } catch (error) {
      console.error('Error editing folder:', error);
    }
  };

  const handleDeleteFolder = async () => {
    if (!contextMenuFolder) return;

    if (window.confirm(`Are you sure you want to delete "${contextMenuFolder.name || contextMenuFolder.folderName}"?`)) {
      try {
        await folderService.deleteFolder(contextMenuFolder.id || contextMenuFolder.folderId);
        
        // Refresh folders
        const foldersResponse = await folderService.getFolderTree();
        setFolders(foldersResponse.data || []);
        
        // If deleted folder was selected, clear selection
        if (selectedFolderId === (contextMenuFolder.id || contextMenuFolder.folderId)) {
          selectFolder(null);
        }
        
        handleContextMenuClose();
      } catch (error) {
        console.error('Error deleting folder:', error);
      }
    }
  };

  const renderFolderTree = (folderList, level = 0) => {
    return folderList.map((folder) => {
      // Check if this is a root folder (folder ID matches user ID)
      const isRootFolder = (folder.id || folder.folderId) === user?.userId;
      
      return (
        <Box key={folder.id || folder.folderId}>
          <MenuItem
            onClick={() => handleFolderSelect(folder)}
            onContextMenu={(e) => handleContextMenu(e, folder)}
            sx={{
              pl: 2 + level * 2,
              py: 1,
              backgroundColor: isRootFolder ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
              fontWeight: isRootFolder ? 600 : 400,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {isRootFolder ? (
                <HomeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              ) : (
                <FolderIcon sx={{ fontSize: 18, color: folder.color || theme.palette.text.secondary }} />
              )}
            </ListItemIcon>
            <ListItemText 
              primary={folder.name || folder.folderName}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { 
                  fontWeight: selectedFolderId === (folder.id || folder.folderId) ? 600 : (isRootFolder ? 500 : 400),
                  color: isRootFolder ? theme.palette.primary.main : 'inherit'
                }
              }}
            />
            {isRootFolder && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 1 }}>
                (Workspace)
              </Typography>
            )}
          </MenuItem>
          {folder.children && folder.children.length > 0 && (
            <Box>
              {renderFolderTree(folder.children, level + 1)}
            </Box>
          )}
        </Box>
      );
    });
  };

  // Render different variants
  const renderSelector = () => {
    // Check if selected folder is root folder
    const isSelectedRoot = selectedFolderInfo && ((selectedFolderInfo.id || selectedFolderInfo.folderId) === user?.userId);
    
    // Choose appropriate icon
    const getIcon = () => {
      if (loading) {
        return <FolderIcon />; // Show folder icon while loading
      }
      if (isSelectedRoot) {
        return <HomeIcon />;
      }
      return selectedFolderInfo ? <FolderOpenIcon /> : <FolderIcon />;
    };

    // Get display name with better loading state
    const getDisplayName = () => {
      if (loading) {
        return "Loading...";
      }
      if (!selectedFolderInfo) {
        // If folders are loaded but no selection, show better placeholder
        return folders.length > 0 ? "Select folder..." : placeholder;
      }
      const name = selectedFolderInfo.name || selectedFolderInfo.folderName;
      return isSelectedRoot ? `${name} (Workspace)` : name;
    };

    // Get compact display name (without workspace label for space)
    const getCompactDisplayName = () => {
      if (loading) {
        return "Loading...";
      }
      if (!selectedFolderInfo) {
        return folders.length > 0 ? "Select..." : "Folder";
      }
      return selectedFolderInfo.name || selectedFolderInfo.folderName;
    };

    switch (variant) {
      case 'chip':
        return (
          <Chip
            icon={getIcon()}
            label={getDisplayName()}
            onClick={handleClick}
            deleteIcon={<ArrowDownIcon />}
            onDelete={handleClick}
            variant="outlined"
            size={size}
            disabled={loading}
            sx={{
              cursor: loading ? 'default' : 'pointer',
              '& .MuiChip-deleteIcon': {
                fontSize: 16,
              },
              '& .MuiChip-icon': {
                color: isSelectedRoot ? theme.palette.primary.main : 'inherit'
              },
              ...sx
            }}
          />
        );
      
      case 'compact':
        return (
          <Button
            variant="text"
            size={size}
            startIcon={getIcon()}
            endIcon={<ArrowDownIcon />}
            onClick={handleClick}
            disabled={loading}
            sx={{
              textTransform: 'none',
              minWidth: 'auto',
              px: 2,
              py: 1,
              borderRadius: 1.5,
              color: 'text.primary',
              backgroundColor: 'transparent',
              border: 'none',
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.08),
                border: 'none'
              },
              '& .MuiButton-startIcon': {
                color: isSelectedRoot ? theme.palette.primary.main : 'inherit'
              },
              '& .MuiButton-endIcon': {
                color: 'text.secondary',
                fontSize: '1rem'
              },
              ...sx
            }}
          >
            <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
              {getCompactDisplayName()}
            </Typography>
          </Button>
        );
      
      default: // 'button'
        return (
          <Button
            variant="outlined"
            size={size}
            startIcon={getIcon()}
            endIcon={<ArrowDownIcon />}
            onClick={handleClick}
            disabled={loading}
            sx={{
              textTransform: 'none',
              justifyContent: 'space-between',
              minWidth: 200,
              '& .MuiButton-startIcon': {
                color: isSelectedRoot ? theme.palette.primary.main : 'inherit'
              },
              ...sx
            }}
          >
            {getDisplayName()}
          </Button>
        );
    }
  };

  return (
    <>
      {renderSelector()}
      
      {/* Main folder selection menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            minWidth: 250,
            mt: 1,
            border: 'none',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            background: theme.palette.background.paper,
            backdropFilter: 'blur(16px)',
            '& .MuiMenuItem-root': {
              borderRadius: 1,
              mx: 1,
              my: 0.5,
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: 'none'
              },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.16)
                }
              }
            },
            '& .MuiDivider-root': {
              mx: 1,
              borderColor: alpha(theme.palette.divider, 0.08)
            }
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0
          }
        }}
      >
        {/* Folder tree - starts with root folder */}
        {loading ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">Loading folders...</Typography>
          </MenuItem>
        ) : folders.length > 0 ? (
          renderFolderTree(folders)
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">No folders found</Typography>
          </MenuItem>
        )}
        
        {/* Create new folder option */}
        {showCreateNew && (
          <>
            <Divider />
            <MenuItem onClick={() => { setCreateDialogOpen(true); setParentFolderId(null); handleClose(); }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CreateFolderIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
              </ListItemIcon>
              <ListItemText 
                primary="Create new folder"
                primaryTypographyProps={{
                  variant: 'body2',
                  color: theme.palette.success.main,
                  fontWeight: 500
                }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Context menu for folder actions */}
      <Menu
        anchorEl={contextMenuAnchor}
        open={contextMenuOpen}
        onClose={handleContextMenuClose}
        PaperProps={{
          sx: { minWidth: 180 }
        }}
      >
        {(() => {
          // Check if context menu folder is root folder
          const isContextRoot = contextMenuFolder && ((contextMenuFolder.id || contextMenuFolder.folderId) === user?.userId);
          
          return (
            <>
              <MenuItem onClick={() => {
                setParentFolderId(contextMenuFolder?.id || contextMenuFolder?.folderId);
                setCreateDialogOpen(true);
                handleContextMenuClose();
              }}>
                <ListItemIcon>
                  <CreateFolderIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary="Create subfolder" />
              </MenuItem>
              
              <MenuItem onClick={() => {
                setEditFolderName(contextMenuFolder?.name || contextMenuFolder?.folderName || '');
                setEditDialogOpen(true);
                handleContextMenuClose();
              }}>
                <ListItemIcon>
                  <EditIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                </ListItemIcon>
                <ListItemText primary={isContextRoot ? "Rename workspace" : "Rename"} />
              </MenuItem>
              
              <Divider />
              
              <MenuItem 
                onClick={isContextRoot ? undefined : handleDeleteFolder} 
                disabled={isContextRoot}
                sx={{ 
                  color: isContextRoot ? theme.palette.text.disabled : theme.palette.error.main 
                }}
              >
                <ListItemIcon>
                  <DeleteIcon sx={{ 
                    fontSize: 18, 
                    color: isContextRoot ? theme.palette.text.disabled : theme.palette.error.main 
                  }} />
                </ListItemIcon>
                <ListItemText 
                  primary={isContextRoot ? "Cannot delete workspace" : "Delete"} 
                  primaryTypographyProps={{
                    color: isContextRoot ? 'text.disabled' : 'inherit'
                  }}
                />
              </MenuItem>
            </>
          );
        })()}
      </Menu>

      {/* Create folder dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {parentFolderId ? 'Create Subfolder' : 'Create New Folder'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newFolderName.trim()) {
                handleCreateFolder(parentFolderId);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleCreateFolder(parentFolderId)} 
            variant="contained"
            disabled={!newFolderName.trim() || createLoading}
          >
            {createLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit folder dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={editFolderName}
            onChange={(e) => setEditFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editFolderName.trim()) {
                handleEditFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEditFolder} 
            variant="contained"
            disabled={!editFolderName.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InlineFolderSelector;
